import api, { compcheckApi } from "configs/api";
import { AppContext } from "context/appContext";
import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setUser } from "slices/authSlice";
import useIndustry from "./useIndustry";
import { useNavigate } from "react-router-dom";
import { RootState } from "configs/store";

interface SignUpField {
  name: string;
  placeholder: string;
  type: string;
  size?: string;
  optionsLabel?: string;
  options?: any[];
}

interface DeviceInfo {
  userAgent: string;
  platform: string;
  language: string;
  browser: string;
  userIP?: string;
}

interface SignUpData {
  image?: File;
  imageURL?: string;
  [key: string]: any;
}

export default function useAuth() {
  const authBaseUrl = "auth/";
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<SignUpData>({});
  const [signupAccountRole, setSignupAccountRole] = useState<string>("company");
  const { user, subdomain } = useSelector((state: RootState) => state.auth);
  const [deviceInfo, setDeviceInfo] = useState<DeviceInfo>({
    userAgent: "",
    platform: "",
    language: "",
    browser: "",
  });

  const { showAlert } = useContext(AppContext);
  const { industries } = useIndustry();

  const normalizedRole = (user?.role || "").toLowerCase();
  const normalizedPosition = (user?.position || "").toLowerCase();

  const isAdmin = normalizedRole === "admin";
  const isCompanyAdmin = normalizedRole === "companyadmin";
  const isFellowAdmin = normalizedRole === "fellowadmin";
  const isCompany = normalizedRole === "company";
  // Align with course service: manager/HR may be role or position (e.g. role "hr" with any position).
  const isManager = normalizedPosition === "manager" || normalizedRole === "manager";
  const isHR = normalizedPosition === "hr" || normalizedRole === "hr";
  const isManagerOrHR = isManager || isHR;
  const isGuest = normalizedPosition === "guest";
  const isSupervisor = normalizedPosition === "supervisor";
  const isEmployee = normalizedPosition === "employee";
  const isCompanyEmployee = normalizedRole === "employee";
  const isRestrictedEmployee = isSupervisor || isGuest || (isEmployee && !isManagerOrHR);
  const isCreator = normalizedRole === "creator";

  const signUpFields: SignUpField[] = [
    { name: "firstname", placeholder: "First Name", type: "text" },
    { name: "lastname", placeholder: "Last Name", type: "text" },
    { name: "email", placeholder: "Email", type: "email" },
    { name: "password", placeholder: "Password", type: "password" },
    { name: "telephone1", placeholder: "Phone Number", type: "text" },
    ...(signupAccountRole === "creator" || signupAccountRole === "employee"
      ? []
      : [{ name: "companyName", placeholder: "Company Name", type: "text" }]),
    ...(signupAccountRole === "creator" || signupAccountRole === "employee"
      ? []
      : [
          {
            placeholder: "Industry",
            name: "industry",
            type: "dropdown",
            size: "medium",
            optionsLabel: "name",
            options: industries,
          },
        ]),
  ].filter((it) => it) as SignUpField[];

  const fetchIP = async (): Promise<string> => {
    try {
      const response = await fetch("https://api.ipify.org?format=json");
      const data = await response.json();
      return data.ip || "0.0.0.0";
    } catch (error) {
      console.error("Failed to fetch IP:", error);
      return "0.0.0.0";
    }
  };

  const fetchUser = async (): Promise<void> => {
    try {
      const response = await api.get(`${authBaseUrl}user/`);
      if (response.data.status) {
        dispatch(setUser(response.data.result));
      } else {
        showAlert(response.data.message, "error", "Server Error");
      }
    } catch (error: any) {
      showAlert(error.message || "Failed to fetch user", "error", "Unexpected Error");
    }
  };

  const emailDubCheck = async (email: string): Promise<boolean> => {
    try {
      const response = await api.get(`${authBaseUrl}user/emailDubCheck?email=${email}`);
      return response.data.status;
    } catch (error: any) {
      showAlert(error.message || "Failed to check email", "error", "Internal Error");
      return false;
    }
  };

  const handleSetTabValue = (_event: React.SyntheticEvent, newValue: string): void => {
    setSignupAccountRole(newValue);
  };

  const handleSignupSubmit = async (
    values: any,
    setSubmitting: (value: boolean) => void
  ): Promise<void> => {
    setLoading(true);
    try {
      const emailAvailable = await emailDubCheck(values.email);
      if (!emailAvailable) {
        showAlert("Email already in use.", "error", "Fields Error");
        setLoading(false);
        return;
      }
      console.log("data", data);
      // if (!data.image) {
      //   showAlert('Profile image is required.', 'error', 'Fields Error');
      //   setLoading(false);
      //   return;
      // }
      let imageURL = "";
      if (data.image) {
        const formData = new FormData();
        formData.set("image", data.image);
        const uploadResponse = await compcheckApi.post(`/api/file/upload`, formData);
        if (uploadResponse.data.status) imageURL = uploadResponse.data?.result;
        else showAlert(uploadResponse.data.message, "error", "Registration Failed!");
      }
      const response = await api.post(`/auth/user/register`, {
        ...values,
        signupAccountRole,
        imageURL: imageURL,
      });
      if (response.data?.status) {
        showAlert(
          "Registration successful. Please login to continue.",
          "success",
          "Registration Success!"
        );
        navigate("/authentication/sign-in");
      } else {
        console.log("else", response);
        showAlert(response.data.message, "error", "Registration Failed!");
      }
    } catch (err: any) {
      const message = err.response
        ? err.response.data?.message
          ? err.response.data?.message
          : err.response.data
        : err.message || "Server error";
      showAlert(
        "Looks like there is some issue with the server. Please try again later.",
        "error",
        "Server Error"
      );
    } finally {
      setSubmitting(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    const getDeviceInfo = async (): Promise<void> => {
      const deviceInfo: DeviceInfo = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        browser: navigator?.userAgent?.includes("Chrome")
          ? "Chrome"
          : navigator?.userAgent?.includes("Firefox")
          ? "Firefox"
          : navigator.userAgent.includes("Safari")
          ? "Safari"
          : "Unknown",
      };
      const userIP = await fetchIP();
      setDeviceInfo({ ...deviceInfo, userIP });
    };
    getDeviceInfo();
  }, []);

  return {
    authBaseUrl,
    deviceInfo,
    fetchUser,
    user,
    subdomain,
    emailDubCheck,
    isCompanyAdmin,
    isFellowAdmin,
    isAdmin,
    isCompany,
    isEmployee,
    isCompanyEmployee,
    isManager,
    isHR,
    isManagerOrHR,
    isSupervisor,
    isGuest,
    isRestrictedEmployee,
    isCreator,
    handleSignupSubmit,
    loading,
    setLoading,
    data,
    setData,
    signupAccountRole,
    setSignupAccountRole,
    handleSetTabValue,
    signUpFields,
  };
}
